import { ChevronDownIcon } from "@primer/octicons-react";
import { ActionList, ActionMenu } from "@primer/react";
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { khoiApi } from '../../api/khoiApi';
import { useCommonContext } from '../../contexts/common';
import { dm_he } from '../../models/response/category/dm_he';
import { actions } from '../../state/actions/actionsWrapper';
import { RootState } from '../../state/reducers';

type ComboboxHeProps = {
	isReadonly?: boolean;
	disabled?: boolean;
	requireKhoi?: boolean;
	value?: number;
	onValueChanged: (id: number, data?: dm_he) => void;
	className?: string;
	isShowClearButton?: boolean;
	preText?: string;
	width?: string | number;
	dm_khoi_id?: number;
	dm_truong_id?: number;
	stylingMode?: 'outlined' | 'filled' | 'underlined';
};

const ComboboxHe = (props: ComboboxHeProps) => {
	const { translate } = useCommonContext();
	const { language } = useSelector((x: RootState) => x.common);
	const categorySource = useSelector((state: RootState) => state.categorySource);
	const dispatch = useDispatch();
	const [source, setSource] = useState<dm_he[]>([]);
	const [loadedKhoiId, setLoadedKhoiId] = useState<number>(0);

	useEffect(() => {
		if (categorySource.dm_hes.length === 0) dispatch(actions.categorySource.loadHeStart());
	}, [categorySource.dm_hes.length, dispatch]);

	useEffect(() => {
		let cancelled = false;
		const khoiId = Number(props.dm_khoi_id) || 0;

		if (khoiId > 0) {
			setLoadedKhoiId(0);
			setSource([]);
			khoiApi
				.selectHe(khoiId)
				.then((res) => {
					if (cancelled) return;
					if (res && res.is_success && Array.isArray(res.data)) {
						setSource(res.data);
					} else {
						setSource([]);
					}
					setLoadedKhoiId(khoiId);
				})
				.catch((error) => {
					if (cancelled) return;
					console.error('ComboboxHe: Error loading hệ', error);
					setSource([]);
					setLoadedKhoiId(khoiId);
				});
		} else {
			setLoadedKhoiId(0);
			if (props.requireKhoi) {
				setSource([]);
			} else if (categorySource.dm_hes.length > 0) {
				if (props.dm_truong_id && props.dm_truong_id !== 0) {
					const filtered = categorySource.dm_hes.filter(
						(x) => !x.dm_truong_id || x.dm_truong_id === props.dm_truong_id
					);
					setSource(filtered);
				} else {
					setSource(categorySource.dm_hes);
				}
			} else {
				setSource([]);
			}
		}

		return () => {
			cancelled = true;
		};
	}, [props.dm_khoi_id, props.dm_truong_id, props.requireKhoi, categorySource.dm_hes]);

	useEffect(() => {
		const khoiId = Number(props.dm_khoi_id) || 0;
		const currentValue = Number(props.value) || 0;

		if (currentValue > 0 && khoiId > 0 && loadedKhoiId === khoiId) {
			const heExists = source.some((x) => x.id === currentValue);
			if (!heExists) {
				props.onValueChanged(0);
			}
		}
	}, [props.dm_khoi_id, props.value, loadedKhoiId, source]);

	const selectedData = useMemo(() => {
		if (props.value && categorySource.dm_hes.length > 0) {
			return categorySource.dm_hes.find((x) => x.id === props.value);
		}
		if (props.value && source.length > 0) {
			return source.find((x) => x.id === props.value);
		}
		return undefined;
	}, [categorySource.dm_hes, source, props.value]);

	return (
		<ActionMenu>
			<ActionMenu.Button
				aria-label="Select school year"
				disabled={props.disabled || props.isReadonly}
				style={{
					width: props.width ?? '100%',
					display: 'flex',
					justifyContent: 'space-between',
					opacity: (props.disabled || props.isReadonly) ? 0.6 : 1,
					cursor: (props.disabled || props.isReadonly) ? 'not-allowed' : 'pointer',
				}}
				trailingAction={ChevronDownIcon}
				className="button-left"
			>
				{selectedData ? (language === "en" ? selectedData.ten_he_en : selectedData.ten_he) : (props.preText || translate("HeCombobox.PlaceHolder"))}
			</ActionMenu.Button>
			<ActionMenu.Overlay width="auto">
				<ActionList selectionVariant="single">
					{props.isShowClearButton &&
						<ActionList.Item key={0} selected={props.value !== undefined && 0 === props.value}
							onSelect={() => {
								props.onValueChanged(0)
							}}
						>
							{language === "en" ? "Chọn hệ" : "Chọn hệ"}
						</ActionList.Item>
					}
					{source && source.map((item) => {
						return (
							<ActionList.Item key={item.id} selected={props.value !== undefined && item.id === props.value}
								onSelect={() => {
									props.onValueChanged(item.id)
								}}
							>
								{language === "en" ? item.ten_he_en : item.ten_he}
							</ActionList.Item>
						);
					})}
				</ActionList>
			</ActionMenu.Overlay>
		</ActionMenu>
	);
};

export default ComboboxHe;
